import { NormalizedExifData } from '../types/exif';
import { PolarstepsStep, PolarstepsTrip } from '../types/polarsteps';

export interface TimezoneResolution {
  timestampUtc: number;
  offsetSeconds: number;
  tier:
    | 'tier1_exif_offset'
    | 'tier2_gps_calibration'
    | 'tier3_step_tz'
    | 'tier3_trip_tz'
    | 'tier4_manual'
    | 'tier4_fallback';
  timezoneId?: string;
}

/**
 * Parses an ISO timezone offset string ("+09:00", "-05:00", "Z", "+05:30", "+12:45") to signed seconds.
 * Returns null if the format is invalid or outside valid ranges.
 */
export function parseExifOffset(offsetStr?: string | null): number | null {
  if (!offsetStr || typeof offsetStr !== 'string') return null;
  const trimmed = offsetStr.trim();
  if (trimmed === 'Z' || trimmed === '+00:00' || trimmed === '-00:00') return 0;

  const match = /^([+-])(\d{1,2}):?(\d{2})?$/.exec(trimmed);
  if (!match) return null;

  const sign = match[1] === '-' ? -1 : 1;
  const hours = parseInt(match[2], 10);
  const minutes = match[3] ? parseInt(match[3], 10) : 0;

  if (hours > 14 || minutes > 59) return null;

  return sign * (hours * 3600 + minutes * 60);
}

// Alias for compatibility
export const parseOffsetStringToSeconds = parseExifOffset;

/**
 * Converts a normalized EXIF date string "YYYY-MM-DD HH:MM:SS" or "YYYY:MM:DD HH:MM:SS" into nominal UTC seconds.
 */
export function parseWallClockToUtcSeconds(dateTimeStr: string, subseconds = 0): number | null {
  if (!dateTimeStr || typeof dateTimeStr !== 'string') return null;
  const match = /^(\d{4})[:\-](\d{2})[:\-](\d{2})\s+(\d{2}):(\d{2}):(\d{2})/.exec(dateTimeStr.trim());
  if (!match) return null;

  const Y = parseInt(match[1], 10);
  const M = parseInt(match[2], 10) - 1;
  const D = parseInt(match[3], 10);
  const h = parseInt(match[4], 10);
  const m = parseInt(match[5], 10);
  const s = parseInt(match[6], 10);

  return Math.floor(Date.UTC(Y, M, D, h, m, s) / 1000) + subseconds;
}

/**
 * Evaluates the exact signed timezone offset in seconds for an IANA timezone at a specific UTC instant.
 * Uses native Intl.DateTimeFormat (zero external library dependencies, 100% DST-accurate).
 */
export function getIanaTimezoneOffsetSeconds(utcEpochSeconds: number, timeZoneId: string): number {
  try {
    const utcDate = new Date(utcEpochSeconds * 1000);
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneId,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });

    const parts = formatter.formatToParts(utcDate);
    const getVal = (type: string) => {
      const part = parts.find((p) => p.type === type);
      return part ? parseInt(part.value, 10) : 0;
    };

    let hour = getVal('hour');
    if (hour === 24) hour = 0;

    const targetUtcEpoch = Date.UTC(
      getVal('year'),
      getVal('month') - 1,
      getVal('day'),
      hour,
      getVal('minute'),
      getVal('second')
    );

    return Math.round((targetUtcEpoch - utcDate.getTime()) / 1000);
  } catch {
    return 0;
  }
}

/**
 * Overload of getTimezoneOffsetSeconds accepting Date or epoch seconds.
 */
export function getTimezoneOffsetSeconds(dateOrEpoch: Date | number, timeZoneId: string): number {
  const epoch = typeof dateOrEpoch === 'number' ? dateOrEpoch : Math.floor(dateOrEpoch.getTime() / 1000);
  return getIanaTimezoneOffsetSeconds(epoch, timeZoneId);
}

/**
 * Tier 2 Auto-Calibration: computes the clock drift in seconds between
 * camera DateTimeOriginal and GPS satellite UTC timestamp.
 */
export function calibrateClockDrift(localCaptureDate: Date | number, gpsUtcTimestamp: number): number {
  let nominalUtcSeconds: number;
  if (typeof localCaptureDate === 'number') {
    nominalUtcSeconds = localCaptureDate;
  } else {
    nominalUtcSeconds =
      Date.UTC(
        localCaptureDate.getFullYear(),
        localCaptureDate.getMonth(),
        localCaptureDate.getDate(),
        localCaptureDate.getHours(),
        localCaptureDate.getMinutes(),
        localCaptureDate.getSeconds()
      ) / 1000;
  }

  return Math.round(nominalUtcSeconds - gpsUtcTimestamp);
}

/**
 * Tier 3 helper: Computes DST-accurate UTC timestamp by iterative offset convergence.
 */
export function resolveWallClockWithIanaTz(
  wallClockUtcSeconds: number,
  timeZoneId: string
): { utc: number; offset: number } {
  let offset = getIanaTimezoneOffsetSeconds(wallClockUtcSeconds, timeZoneId);
  const refinedUtc = wallClockUtcSeconds - offset;
  offset = getIanaTimezoneOffsetSeconds(refinedUtc, timeZoneId);
  return {
    utc: wallClockUtcSeconds - offset,
    offset,
  };
}

/**
 * Complete 4-tier Timezone Resolver
 */
export function resolvePhotoTimezone(
  exif: NormalizedExifData,
  trip?: PolarstepsTrip,
  activeStep?: PolarstepsStep,
  calibratedSkewDelta?: number,
  manualOffsetSeconds?: number
): TimezoneResolution {
  if (!exif.dateTimeOriginal) {
    const fallbackUtc = Math.floor(Date.now() / 1000);
    return { timestampUtc: fallbackUtc, offsetSeconds: 0, tier: 'tier4_fallback' };
  }

  const wallClockUtcSeconds = parseWallClockToUtcSeconds(exif.dateTimeOriginal, exif.subseconds);
  if (wallClockUtcSeconds === null) {
    const fallbackUtc = Math.floor(Date.now() / 1000);
    return { timestampUtc: fallbackUtc, offsetSeconds: 0, tier: 'tier4_fallback' };
  }

  // Tier 4 Override: User manual slider
  if (manualOffsetSeconds !== undefined && manualOffsetSeconds !== null) {
    return {
      timestampUtc: wallClockUtcSeconds - manualOffsetSeconds,
      offsetSeconds: manualOffsetSeconds,
      tier: 'tier4_manual',
    };
  }

  // Tier 1: EXIF 2.31 explicit offset tag
  const exifOffset = parseExifOffset(exif.offsetTime);
  if (exifOffset !== null) {
    return {
      timestampUtc: wallClockUtcSeconds - exifOffset,
      offsetSeconds: exifOffset,
      tier: 'tier1_exif_offset',
    };
  }

  // Tier 2: GPS Satellite Reference Calibration
  if (calibratedSkewDelta !== undefined && Number.isFinite(calibratedSkewDelta)) {
    return {
      timestampUtc: wallClockUtcSeconds - calibratedSkewDelta,
      offsetSeconds: calibratedSkewDelta,
      tier: 'tier2_gps_calibration',
    };
  }

  // Tier 3: Polarsteps Active Step Timezone
  if (activeStep?.timezone_id) {
    const { utc, offset } = resolveWallClockWithIanaTz(wallClockUtcSeconds, activeStep.timezone_id);
    return {
      timestampUtc: utc,
      offsetSeconds: offset,
      tier: 'tier3_step_tz',
      timezoneId: activeStep.timezone_id,
    };
  }

  // Tier 3 Fallback: Polarsteps Trip Timezone
  if (trip?.timezone_id) {
    const { utc, offset } = resolveWallClockWithIanaTz(wallClockUtcSeconds, trip.timezone_id);
    return {
      timestampUtc: utc,
      offsetSeconds: offset,
      tier: 'tier3_trip_tz',
      timezoneId: trip.timezone_id,
    };
  }

  // Tier 4 Final Fallback: UTC (0 offset)
  return {
    timestampUtc: wallClockUtcSeconds,
    offsetSeconds: 0,
    tier: 'tier4_fallback',
  };
}

/**
 * Resolves a photo's true UTC timestamp following the 4-tier hierarchy.
 */
export function resolvePhotoUtcTimestamp(
  photo: {
    dateTimeOriginal: string;
    offsetTimeOriginal?: string;
    offsetTime?: string;
    cameraModel?: string;
  },
  trip?: PolarstepsTrip,
  options?: {
    calibratedDrifts?: Map<string, number>;
    manualOffsetOverrideSeconds?: number;
  }
): number {
  const wallClockUtc = parseWallClockToUtcSeconds(photo.dateTimeOriginal);
  if (wallClockUtc === null) {
    return Math.floor(Date.now() / 1000);
  }

  // Tier 4: Manual override slider takes highest priority
  if (options?.manualOffsetOverrideSeconds !== undefined && options.manualOffsetOverrideSeconds !== null) {
    return wallClockUtc - options.manualOffsetOverrideSeconds;
  }

  // Tier 1: EXIF OffsetTimeOriginal tag
  const exifOffset = parseExifOffset(photo.offsetTimeOriginal || photo.offsetTime);
  if (exifOffset !== null) {
    return wallClockUtc - exifOffset;
  }

  // Tier 2: Reference camera drift calibration
  if (photo.cameraModel && options?.calibratedDrifts?.has(photo.cameraModel)) {
    const drift = options.calibratedDrifts.get(photo.cameraModel)!;
    return wallClockUtc - drift;
  }

  // Tier 3: Step Timezone vs Trip Timezone
  if (trip) {
    // Find matching step if within step start_time and end_time
    if (trip.all_steps && trip.all_steps.length > 0) {
      for (const step of trip.all_steps) {
        if (!step.timezone_id) continue;
        const estOffset = getIanaTimezoneOffsetSeconds(wallClockUtc, step.timezone_id);
        const approxUtc = wallClockUtc - estOffset;
        const start = step.start_time;
        const end = step.end_time || start + 86400;
        if (approxUtc >= start && approxUtc <= end) {
          const { utc } = resolveWallClockWithIanaTz(wallClockUtc, step.timezone_id);
          return utc;
        }
      }
    }

    // Trip timezone fallback
    if (trip.timezone_id) {
      const { utc } = resolveWallClockWithIanaTz(wallClockUtc, trip.timezone_id);
      return utc;
    }
  }

  // Tier 4 Fallback: UTC
  return wallClockUtc;
}
